import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from 'primevue/usetoast'
import apiService from '@/services/api'
import type { User, UpdateUserData, RegisterData, PaginationParams, UserStats } from '@/types'

export const useUsersStore = defineStore('users', () => {
  const users = ref<User[]>([])
  const currentUser = ref<User | null>(null)
  const userStats = ref<UserStats>({ total: 0, active: 0, admins: 0, recent: 0 })
  const isLoading = ref(false)
  const pagination = ref({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  })
  const toast = useToast()

  // Computed properties
  const totalUsers = computed(() => userStats.value.total)
  const activeUsers = computed(() => userStats.value.active)
  const adminUsers = computed(() => userStats.value.admins)
  const recentUsers = computed(() => userStats.value.recent)

  // Actions
  const fetchUsers = async (params?: PaginationParams) => {
    isLoading.value = true
    try {
      const response = await apiService.getUsers(params)
      users.value = response.data
      pagination.value = response.pagination
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch users'
      toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 })
      throw new Error(message)
    } finally {
      isLoading.value = false
    }
  }

  const fetchUserStats = async () => {
    try {
      userStats.value = await apiService.getUserStats()
    } catch (error: any) {
      console.error('Failed to fetch user stats:', error)
    }
  }

  const getUserById = async (id: number): Promise<User | null> => {
    try {
      // Check if user is already in the store
      const existingUser = users.value.find(user => user.id === id)
      if (existingUser) {
        currentUser.value = existingUser
        return existingUser
      }

      // Fetch from API
      const user = await apiService.getUser(id)
      currentUser.value = user
      return user
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch user'
      toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 })
      return null
    }
  }

  const createUser = async (userData: RegisterData): Promise<User | null> => {
    isLoading.value = true
    try {
      const newUser = await apiService.createUser(userData)
      users.value.unshift(newUser)
      userStats.value.total += 1
      if (newUser.status === 'active') userStats.value.active += 1
      if (newUser.role === 'admin') userStats.value.admins += 1
      
      toast.add({ severity: 'success', summary: 'Éxito', detail: 'Usuario creado exitosamente', life: 3000 })
      return newUser
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to create user'
      toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 })
      return null
    } finally {
      isLoading.value = false
    }
  }

  const updateUser = async (id: number, userData: UpdateUserData): Promise<User | null> => {
    isLoading.value = true
    try {
      const updatedUser = await apiService.updateUser(id, userData)
      
      // Update in the users array
      const index = users.value.findIndex(user => user.id === id)
      if (index !== -1) {
        users.value[index] = updatedUser
      }
      
      // Update current user if it's the same
      if (currentUser.value?.id === id) {
        currentUser.value = updatedUser
      }
      
      toast.add({ severity: 'success', summary: 'Éxito', detail: 'Usuario actualizado exitosamente', life: 3000 })
      return updatedUser
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to update user'
      toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 })
      return null
    } finally {
      isLoading.value = false
    }
  }

  const deleteUser = async (id: number): Promise<boolean> => {
    isLoading.value = true
    try {
      await apiService.deleteUser(id)
      
      // Remove from users array
      const index = users.value.findIndex(user => user.id === id)
      if (index !== -1) {
        const deletedUser = users.value[index]
        users.value.splice(index, 1)
        
        // Update stats
        userStats.value.total -= 1
        if (deletedUser.status === 'active') userStats.value.active -= 1
        if (deletedUser.role === 'admin') userStats.value.admins -= 1
      }
      
      // Clear current user if it's the same
      if (currentUser.value?.id === id) {
        currentUser.value = null
      }
      
      toast.add({ severity: 'success', summary: 'Éxito', detail: 'Usuario eliminado exitosamente', life: 3000 })
      return true
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to delete user'
      toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 })
      return false
    } finally {
      isLoading.value = false
    }
  }

  const changeUserPassword = async (id: number, currentPassword: string, newPassword: string): Promise<boolean> => {
    try {
      await apiService.changePassword(id, { currentPassword, newPassword })
      toast.add({ severity: 'success', summary: 'Éxito', detail: 'Contraseña cambiada exitosamente', life: 3000 })
      return true
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to change password'
      toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 })
      return false
    }
  }

  const toggleUserStatus = async (id: number): Promise<User | null> => {
    const user = users.value.find(u => u.id === id)
    if (!user) {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Usuario no encontrado', life: 3000 })
      return null
    }

    const newStatus = user.status === 'active' ? 'inactive' : 'active'
    return await updateUser(id, { status: newStatus })
  }

  const clearUsers = () => {
    users.value = []
    currentUser.value = null
    userStats.value = { total: 0, active: 0, admins: 0, recent: 0 }
    pagination.value = { page: 1, limit: 10, total: 0, totalPages: 0 }
  }

  return {
    users,
    currentUser,
    userStats,
    isLoading,
    pagination,
    totalUsers,
    activeUsers,
    adminUsers,
    recentUsers,
    fetchUsers,
    fetchUserStats,
    getUserById,
    createUser,
    updateUser,
    deleteUser,
    changeUserPassword,
    toggleUserStatus,
    clearUsers
  }
})